// Teams panel — creating teams, and setting the squad each one registers.
//
// THE SQUAD ORDER IS THE POINT. A team registers its players in a fixed board order,
// strongest first, and that order is not decoration: it is what TRF records 013 and 310
// publish, what TeamEngine.validateLineup checks every round's line-up against, and what
// stops a captain moving their strongest player down a board to steal a match. So a squad
// is an ORDERED list with explicit up/down controls, never a set the UI happens to render
// in some order.
//
// Squad membership is deliberately separate from the tournament roster. A player is added
// to the tournament the ordinary way (js/ui/players.js) and then placed in a team, rather
// than teams owning their own player records — which keeps one roster, one set of starting
// numbers, and one rating report.

import { state, saveToLocalStorage, getActiveSettings, getTeams, setTeams, createTeam, teamOfPlayer } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';
import { renderStandings } from './standings.js';
import { submitLineup, fetchLineupsForRound, clearLineup } from '../services/team-lineups.js';

// Teams currently showing their edit form. Held here rather than on the team object so it
// is never persisted or synced — it describes this screen, not the tournament.
const editing = new Set();
let wired = false;

// A CAPTAIN's own choices, held here until Firestore confirms them. A captain cannot write
// the tournament document at all (see js/services/team-lineups.js for why), so their
// line-up does not live on the team object the way an arbiter's does — this is what the
// picker renders back to them in the meantime.
const captainDraft = new Map();

// Line-ups captains have submitted, as the ARBITER last fetched them:
// { [teamId]: { id, round, lineup, captainEmail } }. Empty until fetched, and re-fetched
// whenever the panel is opened for a round.
let submitted = {};
let submittedRound = null;

function isTeamTournament() {
    return getActiveSettings().format === 'team';
}

// An Arena (js/core/arena-engine.js) has teams too, but simpler ones: the games are
// individual, so a team is a name and the players in it. No board order, no line-ups and
// no captains; see renderArenaTeams below.
function isArena() {
    return getActiveSettings().format === 'arena';
}

function playerById(id) {
    return state.players.find(p => p.id === id);
}

/**
 * Players not yet in any squad, in starting-number order.
 *
 * A player belongs to at most one team, and the squad lists are the only record of that,
 * so "unassigned" is computed rather than stored and cannot drift out of step.
 */
function unassignedPlayers(teams) {
    const taken = new Set(teams.flatMap(t => t.squad || []));
    return state.players
        .filter(p => !taken.has(p.id))
        .sort((a, b) => (parseInt(a.startNo, 10) || 9999) - (parseInt(b.startNo, 10) || 9999));
}

// ---------------------------------------------------------------------------
// Who is looking
//
// A team captain is not an account type — there is no "captain" role claim and there
// should not be one, because being a captain is a fact about ONE TEAM IN ONE
// TOURNAMENT, not about a person. So it is decided by matching the signed-in user
// against the team: by uid once they have been linked, or by the verified address the
// arbiter recorded.
//
// The address comparison is lower-cased on both sides because that is the invariant the
// whole app relies on (see the note on verifiedEmail() in firestore.rules); comparing
// raw would silently fail for anyone whose address the arbiter typed with a capital.
// ---------------------------------------------------------------------------

function currentUser() {
    try {
        return (window.authApp && window.authApp.isAuthenticated())
            ? window.authApp.getCurrentUser()
            : null;
    } catch (e) {
        return null;
    }
}

export function isCaptainOf(team) {
    const u = currentUser();
    if (!u || !team) return false;
    if (team.captainUid && team.captainUid === u.uid) return true;
    const mine = (u.email || '').trim().toLowerCase();
    if (!mine) return false;
    // The arbiters' own document carries the address itself.
    if (team.captainEmail) return team.captainEmail.toLowerCase() === mine;
    // A captain reaches the tournament as a spectator, through the public mirror, which
    // carries only a hash of the address (functions/lib/public-tournament.js) so that no
    // reader can harvest it. Matching needs this account's own hash, which SubtleCrypto
    // computes asynchronously: until it arrives the answer is "not yet", and the panel is
    // redrawn when it does.
    if (team.captainEmailHash) {
        ensureMyEmailHash(mine);
        return myEmailHash.email === mine && !!myEmailHash.hash && myEmailHash.hash === team.captainEmailHash;
    }
    return false;
}

// SHA-256 hex of the signed-in address, computed once per address.
let myEmailHash = { email: '', hash: '' };

function ensureMyEmailHash(email) {
    if (myEmailHash.email === email || !(window.crypto && window.crypto.subtle)) return;
    myEmailHash = { email, hash: '' };
    window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))
        .then(buf => {
            if (myEmailHash.email !== email) return; // signed in as someone else meanwhile
            myEmailHash.hash = [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
            renderTeams();
        })
        .catch(() => { /* no match is the safe answer */ });
}

/** The arbiter's view: anything that is not the read-only spectator view. */
function isArbiterView() {
    return !state.isSpectatorMode;
}

/** Teams the person at this screen captains. Empty for the arbiter's own view. */
function myTeams(teams) {
    return teams.filter(t => isCaptainOf(t));
}

/**
 * The teams to show.
 *
 * An arbiter sees the whole field. A captain — who reaches a public tournament as a
 * spectator — sees only their own team, because the panel is where line-ups are set and
 * another team's selection for a round nobody has played is not theirs to read.
 */
function visibleTeams(teams) {
    return isArbiterView() ? teams : myTeams(teams);
}

/**
 * May the person at this screen edit THIS team's line-up?
 *
 * The arbiter always may. A captain may only for their own team, and only when the
 * competition lets captains set line-ups at all.
 */
function canEditLineup(team) {
    if (isArbiterView()) return true;
    return getActiveSettings().teamSettings.captainsSetLineups && isCaptainOf(team);
}

/**
 * The round a line-up is currently being chosen FOR: the next one to be paired.
 *
 * Line-ups are only ever edited ahead of the pairing. Once a round is paired its
 * line-up has become the board pairings themselves, and changing it afterwards is an
 * edit to those pairings — a different operation, with its own screen — not a line-up
 * change. Returns null when the tournament has no round left to pair.
 */
function nextRoundNo() {
    const settings = getActiveSettings();
    const total = parseInt(settings.roundsCount, 10) || 0;
    const next = state.rounds.length + 1;
    return (total > 0 && next > total) ? null : next;
}

/** The line-up a team will field next round: the captain's choice, or the squad's top. */
function lineupFor(team, boards) {
    // A captain's own unsaved choice wins over anything stored, so the picker reflects
    // what they just clicked even before Firestore has acknowledged it.
    if (!isArbiterView() && captainDraft.has(team.id)) {
        return captainDraft.get(team.id).slice(0, boards);
    }
    const chosen = (team.lineups || {})[nextRoundNo()];
    if (Array.isArray(chosen) && chosen.some(id => id)) return chosen.slice(0, boards);
    return window.TeamEngine.defaultLineup(
        team.squad || [], boards,
        (id) => { const p = playerById(id); return !p || p.status !== false; }
    );
}

function lineupSectionHtml(team, boards) {
    const round = nextRoundNo();
    if (round === null) return '';

    const squad = team.squad || [];
    // Nothing to choose while the squad only just fills the boards — the line-up is
    // forced, and a picker offering one option is noise.
    if (squad.length === 0) return '';

    const editable = canEditLineup(team);
    const current = lineupFor(team, boards);
    const forced = squad.length <= boards;

    const slots = Array.from({ length: boards }, (_, i) => {
        const selected = current[i] || '';
        const options = [`<option value="">${tr('— empty —')}</option>`].concat(
            squad.map(pid => {
                const p = playerById(pid);
                const label = p ? formatPlayerName(p, true) + (p.rating ? ` (${p.rating})` : '') : tr('Unknown');
                return `<option value="${escapeHtml(pid)}" ${pid === selected ? 'selected' : ''}>${escapeHtml(label)}</option>`;
            })
        ).join('');
        return `
            <div style="display: flex; align-items: center; gap: 0.5rem;">
                <span style="width: 4.5rem; color: var(--text-muted); font-size: 0.8rem;">${tr('Board {n}', { n: i + 1 })}</span>
                <select data-lineup-team="${escapeHtml(team.id)}" data-board="${i}" style="flex: 1; max-width: 280px;" ${editable ? '' : 'disabled'}>
                    ${options}
                </select>
            </div>`;
    }).join('');

    const errors = window.TeamEngine.validateLineup(
        squad, current, boards,
        { reservesBottomOnly: getActiveSettings().teamSettings.reservesBottomOnly }
    );

    return `
        <div style="border-top: 1px dashed var(--border-color); margin-top: 0.75rem; padding-top: 0.75rem;" data-lineup-for="${escapeHtml(team.id)}">
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; flex-wrap: wrap;">
                <strong style="font-size: 0.85rem;">${escapeHtml(tr('Line-up for round {n}', { n: round }))}</strong>
                ${forced ? `<span style="color: var(--text-muted); font-size: 0.75rem;">${tr('the whole squad plays')}</span>` : ''}
                ${editable ? `
                    <span style="margin-left: auto; display: flex; gap: 0.4rem;">
                        <button type="button" class="btn-outline btn-small" data-team-action="lineup-reset" data-team="${escapeHtml(team.id)}" title="${tr('Field the top of the squad')}">${tr('Use squad order')}</button>
                    </span>`
                    : `<span style="margin-left: auto; color: var(--text-muted); font-size: 0.75rem;">${tr('read only')}</span>`}
            </div>
            <div style="display: flex; flex-direction: column; gap: 0.35rem;">${slots}</div>
            ${errors.ok ? '' : `
                <div style="margin-top: 0.5rem; color: var(--danger); font-size: 0.8rem;">
                    <i class="ri-alert-line"></i> ${errors.errors.map(e => escapeHtml(tr(e))).join('<br>')}
                </div>`}
        </div>`;
}

function squadRowHtml(team, playerId, index, boards) {
    const p = playerById(playerId);
    const btn = (action, icon, title, extra = '') =>
        `<button type="button" class="btn-icon-small" data-team-action="${action}" data-team="${escapeHtml(team.id)}" data-player="${escapeHtml(playerId)}" title="${title}" ${extra}><i class="${icon}"></i></button>`;

    // A squad entry whose player has left the roster: shown rather than silently dropped,
    // so the arbiter can see why the team is short instead of wondering.
    if (!p) {
        return `
            <li style="display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.5rem; color: var(--danger);">
                <span style="width: 1.5rem; text-align: right;">${index + 1}.</span>
                <span style="flex: 1;">${tr('This player is no longer in the tournament')}</span>
                ${btn('squad-remove', 'ri-close-line', tr('Remove'))}
            </li>`;
    }

    // Anyone below the match width is a reserve. Marked, because it is the first thing
    // anyone asks of this list.
    const isReserve = index >= boards;
    return `
        <li style="display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.5rem; border-radius: 6px; ${isReserve ? 'opacity: 0.7;' : ''}">
            <span style="width: 1.5rem; text-align: right; color: var(--text-muted); font-size: 0.85rem;">${index + 1}.</span>
            <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                ${escapeHtml(formatPlayerName(p, true))}
                ${p.rating ? `<span style="color: var(--text-muted); font-size: 0.8rem;"> (${escapeHtml(String(p.rating))})</span>` : ''}
                ${isReserve ? `<span class="status-badge muted" style="margin-left: 0.4rem; font-size: 0.7rem;">${tr('reserve')}</span>` : ''}
            </span>
${isArbiterView() ? `
            ${btn('squad-up', 'ri-arrow-up-line', tr('Move up'), index === 0 ? 'disabled' : '')}
            ${btn('squad-down', 'ri-arrow-down-line', tr('Move down'), index === (team.squad || []).length - 1 ? 'disabled' : '')}
            ${btn('squad-remove', 'ri-close-line', tr('Remove from team'))}` : ''}
        </li>`;
}

function editFormHtml(team) {
    const field = (id, label, value, placeholder = '') => `
        <div class="form-group" style="margin-bottom: 0;">
            <label for="team-edit-${id}-${escapeHtml(team.id)}" style="font-size: 0.8rem;">${label}</label>
            <input type="text" id="team-edit-${id}-${escapeHtml(team.id)}" data-team-field="${id}" value="${escapeHtml(value || '')}" placeholder="${escapeHtml(placeholder)}">
        </div>`;

    return `
        <div class="card-body" style="padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.75rem;" data-team-edit="${escapeHtml(team.id)}">
            <div class="form-row">
                ${field('name', tr('Team name'), team.name)}
                ${field('club', tr('Club'), team.club, tr('optional'))}
            </div>
${isArena() ? '' : `
            <div class="form-row">
                ${field('captainName', tr('Team captain'), team.captainName, tr('optional'))}
                ${field('captainEmail', tr('Captain e-mail'), team.captainEmail, tr('the address they sign in with'))}
            </div>`}
            <div style="display: flex; gap: 0.5rem;">
                <button type="button" class="btn-primary btn-small" data-team-action="save" data-team="${escapeHtml(team.id)}">${tr('Save')}</button>
                <button type="button" class="btn-outline btn-small" data-team-action="cancel-edit" data-team="${escapeHtml(team.id)}">${tr('Cancel')}</button>
            </div>
        </div>`;
}

function teamCardHtml(team, teams, boards) {
    const squad = team.squad || [];
    const free = unassignedPlayers(teams);
    const short = squad.length < boards;

    const addOptions = free.length === 0
        ? `<option value="">${tr('Every player is already in a team')}</option>`
        : `<option value="">${tr('Add a player…')}</option>` + free.map(p =>
            `<option value="${escapeHtml(p.id)}">${escapeHtml(formatPlayerName(p, true))}${p.rating ? ` (${escapeHtml(String(p.rating))})` : ''}</option>`
        ).join('');

    const body = editing.has(team.id) ? editFormHtml(team) : `
        <div class="card-body" style="padding: 0.75rem 1.25rem;">
            <div style="color: var(--text-muted); font-size: 0.8rem; margin-bottom: 0.4rem;">
                <i class="ri-user-star-line"></i>
                ${team.captainName
                    ? escapeHtml(tr('Captain: {name}', { name: team.captainName }))
                      + (team.captainEmail ? ` <span style="opacity: 0.7;">${escapeHtml(team.captainEmail)}</span>` : '')
                    : tr('No captain assigned')}
            </div>
            <ol style="list-style: none; padding: 0; margin: 0 0 0.5rem;">
                ${squad.length === 0
                    ? `<li style="color: var(--text-muted); padding: 0.5rem;">${tr('No players yet — the order you add them is the board order.')}</li>`
                    : squad.map((pid, i) => squadRowHtml(team, pid, i, boards)).join('')}
            </ol>
${isArbiterView() ? `
            <select data-team-action="squad-add" data-team="${escapeHtml(team.id)}" style="max-width: 340px;" ${free.length === 0 ? 'disabled' : ''}>
                ${addOptions}
            </select>` : ''}
            ${lineupSectionHtml(team, boards)}
        </div>`;

    return `
        <div class="card glass" style="margin-bottom: 1rem;">
            <div class="card-header" style="padding: 0.75rem 1.25rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
                <span class="status-badge" style="background: var(--primary); color: #fff;" title="${tr('Team pairing number')}">${team.teamNo}</span>
                <strong style="font-size: 1.05rem;">${escapeHtml(team.name)}</strong>
                ${team.club && team.club !== team.name ? `<span style="color: var(--text-muted); font-size: 0.85rem;">${escapeHtml(team.club)}</span>` : ''}
                ${short ? `<span class="status-badge warning" title="${tr('Fewer players registered than the match has boards')}">${escapeHtml(tr('{n} of {b}', { n: squad.length, b: boards }))}</span>` : ''}
${isArbiterView() ? `
                <span style="margin-left: auto; display: flex; gap: 0.4rem;">
                    <button type="button" class="btn-outline btn-small" data-team-action="edit" data-team="${escapeHtml(team.id)}"><i class="ri-edit-line"></i> ${tr('Edit')}</button>
                    <button type="button" class="btn-outline btn-small" data-team-action="delete" data-team="${escapeHtml(team.id)}" style="color: var(--danger); border-color: var(--danger);"><i class="ri-delete-bin-line"></i></button>
                </span>` : ''}
            </div>
            ${body}
        </div>`;
}

/**
 * The banner that tells an arbiter a captain has sent in a line-up.
 *
 * A submission is never applied on its own. Overwriting the arbiter's own selection
 * because a captain saved something a moment earlier is exactly the kind of surprise that
 * loses trust in a pairing program, so it waits behind a button.
 */
function submissionsBannerHtml(teams) {
    const round = nextRoundNo();
    if (round === null || !isArbiterView()) return '';

    const pending = Object.entries(submitted)
        .filter(([teamId]) => teams.some(t => t.id === teamId));
    if (pending.length === 0) return '';

    const names = pending
        .map(([teamId]) => (teams.find(t => t.id === teamId) || {}).name)
        .filter(Boolean)
        .join(', ');

    return `
        <div style="display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 1rem; margin-bottom: 1rem; border: 1px solid var(--primary); border-radius: 8px; flex-wrap: wrap;">
            <i class="ri-inbox-line" style="color: var(--primary);"></i>
            <span style="font-size: 0.85rem;">
                ${escapeHtml(tr('Line-ups sent in for round {n} by: {names}', { n: round, names }))}
            </span>
            <button type="button" class="btn-primary btn-small" data-team-action="apply-submissions" style="margin-left: auto;">
                ${tr('Apply them')}
            </button>
        </div>`;
}

// ---------------------------------------------------------------------------
// Arena teams
//
// The same team records a team tournament keeps (createTeam), with only the name, the club
// and the squad in use. The squad's order means nothing here, so members are listed by
// starting number. Everyone may look: in a public Arena, which team a player is in is as
// public as their club, and the pairing list already shows it.
// ---------------------------------------------------------------------------

/** Players not yet in a team who have a club, which is what Teams from Clubs works from. */
function clubPlayersWithoutTeam(teams) {
    return unassignedPlayers(teams).filter(p => String(p.club || '').trim());
}

function arenaMemberRowHtml(team, playerId) {
    const p = playerById(playerId);
    const remove = isArbiterView()
        ? `<button type="button" class="btn-icon-small" data-team-action="squad-remove" data-team="${escapeHtml(team.id)}" data-player="${escapeHtml(playerId)}" title="${tr('Remove from team')}"><i class="ri-close-line"></i></button>`
        : '';
    if (!p) {
        return `
            <li style="display: flex; align-items: center; gap: 0.5rem; padding: 0.2rem 0; color: var(--danger); font-size: 0.85rem;">
                <span style="flex: 1;">${tr('This player is no longer in the tournament')}</span>${remove}
            </li>`;
    }
    return `
        <li style="display: flex; align-items: center; gap: 0.5rem; padding: 0.2rem 0; font-size: 0.85rem;">
            <span style="flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                ${escapeHtml(formatPlayerName(p, true))}${p.rating ? ` <span style="color: var(--text-muted); font-size: 0.75rem;">(${escapeHtml(String(p.rating))})</span>` : ''}
            </span>${remove}
        </li>`;
}

function arenaTeamCardHtml(team, teams) {
    const bySnr = (id) => parseInt((playerById(id) || {}).startNo, 10) || 9999;
    const members = (team.squad || []).slice().sort((a, b) => bySnr(a) - bySnr(b));
    const free = unassignedPlayers(teams);
    const arbiter = isArbiterView();

    const body = editing.has(team.id) ? editFormHtml(team) : `
        <div class="card-body" style="padding: 0.5rem 1rem 0.75rem;">
            <ul style="list-style: none; padding: 0; margin: 0 0 0.5rem;">
                ${members.length === 0
                    ? `<li style="color: var(--text-muted); font-size: 0.85rem; padding: 0.2rem 0;">${tr('No players yet.')}</li>`
                    : members.map(id => arenaMemberRowHtml(team, id)).join('')}
            </ul>
${arbiter ? `
            <select data-team-action="squad-add" data-team="${escapeHtml(team.id)}" style="width: 100%;" ${free.length === 0 ? 'disabled' : ''}>
                ${free.length === 0
                    ? `<option value="">${tr('Every player is already in a team')}</option>`
                    : `<option value="">${tr('Add a player…')}</option>` + free.map(p =>
                        `<option value="${escapeHtml(p.id)}">${escapeHtml(formatPlayerName(p, true))}${p.club ? ` (${escapeHtml(p.club)})` : ''}</option>`
                    ).join('')}
            </select>` : ''}
        </div>`;

    return `
        <div class="card glass" style="margin: 0;">
            <div class="card-header" style="padding: 0.6rem 1rem; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                <strong style="font-size: 1rem;">${escapeHtml(team.name)}</strong>
                <span class="status-badge muted" title="${tr('Players')}">${members.length}</span>
                ${team.club && team.club !== team.name ? `<span style="color: var(--text-muted); font-size: 0.8rem;">${escapeHtml(team.club)}</span>` : ''}
${arbiter ? `
                <span style="margin-left: auto; display: flex; gap: 0.3rem;">
                    <button type="button" class="btn-icon-small" data-team-action="edit" data-team="${escapeHtml(team.id)}" title="${tr('Edit')}"><i class="ri-edit-line"></i></button>
                    <button type="button" class="btn-icon-small" data-team-action="delete" data-team="${escapeHtml(team.id)}" title="${tr('Remove Team')}" style="color: var(--danger);"><i class="ri-delete-bin-line"></i></button>
                </span>` : ''}
            </div>
            ${body}
        </div>`;
}

function renderArenaTeams(panel) {
    const teams = getTeams();
    const arbiter = isArbiterView();
    if (!arbiter && teams.length === 0) { panel.innerHTML = ''; return; }

    const unassigned = unassignedPlayers(teams);
    const fromClubs = clubPlayersWithoutTeam(teams);
    const body = teams.length === 0
        ? `<p style="color: var(--text-muted); margin: 0;">${tr('No teams yet. Add a team and place players from the roster below in it, or make one team per club with Teams from Clubs. Each round, players are paired with someone from another team wherever they can be.')}</p>`
        : `<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 1rem; align-items: start;">
               ${teams.slice()
                   .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nb'))
                   .map(t => arenaTeamCardHtml(t, teams)).join('')}
           </div>`;

    panel.innerHTML = `
        <div class="card glass" style="margin-bottom: 1.5rem;">
            <div class="card-header" style="padding: 1rem 1.25rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
                <h3 style="margin: 0; font-size: 1.1rem;"><i class="ri-team-line"></i> ${tr('Teams')}</h3>
                <span style="color: var(--text-muted); font-size: 0.85rem;">
                    ${escapeHtml(tr('{n} teams', { n: teams.length }))}
                    ${arbiter && unassigned.length > 0 ? ` • ${escapeHtml(tr('{n} players not in a team', { n: unassigned.length }))}` : ''}
                </span>
${arbiter ? `
                <span style="margin-left: auto; display: flex; gap: 0.5rem; flex-wrap: wrap;">
                    ${fromClubs.length > 0 ? `
                        <button type="button" class="btn-outline btn-small" data-team-action="from-clubs" title="${tr('One team per club, for the players not yet in a team')}">
                            <i class="ri-community-line"></i> ${tr('Teams from Clubs')}
                        </button>` : ''}
                    <button type="button" class="btn-primary btn-small" data-team-action="add">
                        <i class="ri-add-line"></i> ${tr('Add Team')}
                    </button>
                </span>` : ''}
            </div>
            <div class="card-body" style="padding: 1rem 1.25rem;">${body}</div>
        </div>`;
}

/**
 * Put every player who is in no team yet, and has a club, into a team named after the
 * club: an existing team of that name (or club) if there is one, else a new one.
 *
 * Clubs are matched the way placeInTeamByName matches a typed team name (js/state.js):
 * case and spacing ignored. Players without a club are left for the arbiter to place.
 */
async function teamsFromClubs() {
    const key = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const waiting = clubPlayersWithoutTeam(getTeams());
    if (waiting.length === 0) return;

    const clubs = new Map();
    waiting.forEach(p => {
        const k = key(p.club);
        if (!clubs.has(k)) clubs.set(k, { name: String(p.club).trim(), ids: [] });
        clubs.get(k).ids.push(p.id);
    });

    const confirmed = await window.Dialog.confirm(
        tr('Place {n} players in teams by club, one team per club ({t} in all)? Players without a club are left as they are.',
            { n: waiting.length, t: clubs.size }),
        tr('Teams from Clubs'), tr('Make Teams'), false, tr('Cancel')
    );
    if (!confirmed) return;

    clubs.forEach(({ name, ids }, k) => {
        let team = getTeams().find(t => key(t.name) === k || key(t.club) === k);
        if (!team) {
            team = createTeam({ name, club: name });
            // One at a time, so each new team gets the next team number.
            setTeams([...getTeams(), team]);
        }
        team.squad = [...(team.squad || []), ...ids.filter(id => !(team.squad || []).includes(id))];
    });
    commit();
}

export function renderTeams() {
    const panel = document.getElementById('teams-panel');
    if (!panel) return;

    if (isArena()) {
        renderArenaTeams(panel);
        wire();
        return;
    }
    if (!isTeamTournament()) { panel.innerHTML = ''; return; }

    const teams = getTeams();
    const boards = getActiveSettings().teamSettings.boards;
    const unassigned = unassignedPlayers(teams);
    const shown = visibleTeams(teams);
    const arbiter = isArbiterView();

    // A spectator who captains nothing has no business with this panel at all — and a
    // captain sees only their own team, so an empty list here means "not a captain".
    if (!arbiter && shown.length === 0) { panel.innerHTML = ''; return; }

    // Fire-and-forget: the fetch re-renders when it lands, so the panel paints
    // immediately rather than waiting on the network.
    if (arbiter) refreshSubmissions();

    const header = arbiter ? `
        <div class="card-header" style="padding: 1rem 1.25rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
            <h3 style="margin: 0; font-size: 1.1rem;"><i class="ri-team-line"></i> ${tr('Teams')}</h3>
            <span style="color: var(--text-muted); font-size: 0.85rem;">
                ${escapeHtml(tr('{n} teams', { n: teams.length }))} •
                ${escapeHtml(tr('{b} boards per match', { b: boards }))}
                ${unassigned.length > 0 ? ` • ${escapeHtml(tr('{n} players not in a team', { n: unassigned.length }))}` : ''}
            </span>
            <button type="button" class="btn-primary btn-small" data-team-action="add" style="margin-left: auto;">
                <i class="ri-add-line"></i> ${tr('Add Team')}
            </button>
        </div>` : `
        <div class="card-header" style="padding: 1rem 1.25rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
            <h3 style="margin: 0; font-size: 1.1rem;"><i class="ri-user-star-line"></i> ${tr('My Team')}</h3>
            <span style="color: var(--text-muted); font-size: 0.85rem;">
                ${escapeHtml(tr('{b} boards per match', { b: boards }))}
            </span>
        </div>`;

    const body = arbiter && teams.length === 0
        ? `<p style="color: var(--text-muted); margin: 0;">${tr('No teams yet. Add a team, then place players from the roster below into its squad — the order you place them in is the board order.')}</p>`
        : shown
            .slice()
            .sort((a, b) => (parseInt(a.teamNo, 10) || 0) - (parseInt(b.teamNo, 10) || 0))
            .map(t => teamCardHtml(t, teams, boards)).join('');

    panel.innerHTML = `
        <div class="card glass" style="margin-bottom: 1.5rem;">
            ${header}
            <div class="card-body" style="padding: 1rem 1.25rem;">
                ${submissionsBannerHtml(teams)}
                ${body}
            </div>
        </div>`;

    wire();
}

/**
 * Pull the captains' submissions for the round being prepared.
 *
 * Silent on failure: an arbiter working offline, or on a tournament that has never been
 * saved to the cloud, has no submissions to see and should not be shown an error about it.
 * Re-renders only when something actually changed, so it cannot loop against itself.
 */
async function refreshSubmissions() {
    const round = nextRoundNo();
    const tournamentId = state.activeTournament && state.activeTournament.id;
    if (round === null || !tournamentId) return;
    if (!window.authApp || !window.authApp.isAuthenticated()) return;
    if (submittedRound === round && Object.keys(submitted).length > 0) return;

    try {
        const found = await fetchLineupsForRound(tournamentId, round);
        const changed = JSON.stringify(found) !== JSON.stringify(submitted);
        submitted = found;
        submittedRound = round;
        if (changed) renderTeams();
    } catch (e) {
        // Not an error worth showing: most tournaments have no submissions at all.
        console.debug('Could not load submitted line-ups:', e && e.message);
    }
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function commit() {
    saveToLocalStorage();
    renderTeams();
    renderStandings();
}

async function addTeam() {
    const name = await window.Dialog.text(
        tr('What is the team called?'), tr('Add Team'), '', tr('Add'), tr('e.g. Oslo Schakselskap')
    );
    if (!name) return;
    setTeams([...getTeams(), createTeam({ name })]);
    commit();
}

function saveEdit(team) {
    const form = document.querySelector(`[data-team-edit="${CSS.escape(team.id)}"]`);
    if (!form) return;
    const read = (field) => form.querySelector(`[data-team-field="${field}"]`)?.value.trim() ?? '';

    const name = read('name');
    if (name) team.name = name;
    team.club = read('club');
    // The captain is the competition's own role — C.04.6 says nothing about captains.
    // The address is what they later sign in with, so it is stored lower-cased to match
    // every other address comparison in the app (see verifiedEmail() in firestore.rules).
    team.captainName = read('captainName');
    team.captainEmail = read('captainEmail').toLowerCase();

    editing.delete(team.id);
    commit();
}

async function deleteTeam(team) {
    // Deleting a team that has played would strand its results: the rounds still name it,
    // and the standings would count matches against a team that no longer exists. Refused
    // rather than half-done.
    const hasPlayed = state.rounds.some(r =>
        (r.matches || []).some(m => m.whiteTeam === team.id || m.blackTeam === team.id));
    if (hasPlayed) {
        await window.Dialog.error(
            tr('{name} has already played a match, so it cannot be removed.', { name: team.name }),
            tr('Team Has Results')
        );
        return;
    }

    const confirmed = await window.Dialog.confirm(
        tr('Remove {name}? Its players stay in the tournament and become available for another team.', { name: team.name }),
        tr('Remove Team'), tr('Remove'), true
    );
    if (!confirmed) return;

    setTeams(getTeams().filter(t => t.id !== team.id));
    editing.delete(team.id);
    commit();
}

function moveInSquad(team, playerId, delta) {
    const squad = team.squad || [];
    const i = squad.indexOf(playerId);
    const j = i + delta;
    if (i === -1 || j < 0 || j >= squad.length) return;
    [squad[i], squad[j]] = [squad[j], squad[i]];
    commit();
}

async function removeFromSquad(team, playerId) {
    // Once a round is paired, the squad order is what that round's line-up was checked
    // against, so reordering it afterwards cannot change what was already played. Warned
    // about rather than blocked — an arbiter fixing a real mistake still has to be able to.
    // An Arena has no line-ups: a team only steers the rounds still to be paired.
    if (state.rounds.length > 0 && !isArena()) {
        const ok = await window.Dialog.confirm(
            tr('Rounds have already been paired. Changing the squad now will not change any line-up already played. Continue?'),
            tr('Squad Already Used'), tr('Continue'), false, tr('Cancel'), 'warning'
        );
        if (!ok) return;
    }
    team.squad = (team.squad || []).filter(id => id !== playerId);
    commit();
}

/**
 * Store the line-up chosen for the next round.
 *
 * Written even when it is illegal, and flagged rather than refused: a captain filling
 * four boards will pass through invalid intermediate states on the way (two players on
 * one board while they swap), and refusing each keystroke makes the picker unusable.
 * The pairing path is where legality actually has to hold, and generateNextTeamRound
 * checks it there.
 */
function setLineupSlot(team, boardIndex, playerId) {
    const round = nextRoundNo();
    if (round === null) return;
    const boards = getActiveSettings().teamSettings.boards;

    const lineup = lineupFor(team, boards).slice();
    while (lineup.length < boards) lineup.push(null);

    // Picking someone already on another board SWAPS the two, rather than fielding them
    // twice — which is what the arbiter meant, and saves an explicit "clear the other
    // board first" step every time a captain reorders.
    const previous = lineup.indexOf(playerId);
    const displaced = lineup[boardIndex] || null;
    if (playerId && previous !== -1 && previous !== boardIndex) lineup[previous] = displaced;

    lineup[boardIndex] = playerId || null;

    // The ARBITER edits the tournament directly. A CAPTAIN cannot — they have no write
    // access to the tournament document, by design — so their choice goes to their own
    // line-up document and the arbiter applies it. See js/services/team-lineups.js.
    if (isArbiterView()) {
        team.lineups = { ...(team.lineups || {}), [round]: lineup };
        commit();
        return;
    }

    captainDraft.set(team.id, lineup);
    renderTeams();
    submitLineup({
        tournamentId: state.activeTournament.id,
        teamId: team.id,
        round,
        lineup
    }).catch(async (e) => {
        // The draft is dropped so the picker stops showing a choice that was never
        // recorded — a captain who thinks they submitted a line-up and did not is worse
        // off than one who is told plainly.
        captainDraft.delete(team.id);
        renderTeams();
        await window.Dialog.error(
            tr('Your line-up could not be saved. You may no longer be this team\'s captain, or the round has already been paired.'),
            tr('Not Saved')
        );
    });
}

/**
 * Copy the captains' submissions onto the teams, as the arbiter.
 *
 * Each applied submission is cleared, so the banner reflects what is still outstanding
 * rather than everything ever sent.
 */
async function applySubmissions() {
    const round = nextRoundNo();
    if (round === null) return;
    const teams = getTeams();
    const applied = [];

    Object.entries(submitted).forEach(([teamId, sub]) => {
        const team = teams.find(t => t.id === teamId);
        if (!team) return;
        team.lineups = { ...(team.lineups || {}), [round]: sub.lineup };
        applied.push({ teamId, name: team.name });
    });

    if (applied.length === 0) return;
    saveToLocalStorage();

    // Clearing is best-effort: the line-ups are already on the teams, and a failed delete
    // would only mean the banner offers to apply the same thing again.
    await Promise.all(applied.map(a =>
        clearLineup(state.activeTournament.id, a.teamId, round)
            .catch(e => console.debug('Could not clear a submitted line-up:', e && e.message))
    ));

    submitted = {};
    renderTeams();
    renderStandings();
    await window.Dialog.success(
        tr('Applied {n} line-up(s) for round {r}.', { n: applied.length, r: round }),
        tr('Line-ups Applied')
    );
}

/** Drop the chosen line-up and go back to the top of the squad. */
function resetLineup(team) {
    const round = nextRoundNo();
    if (round === null) return;

    if (!isArbiterView()) {
        // A captain's reset is itself a submission — the squad order, stated explicitly.
        // Deleting their document instead would read as "submitted nothing", which the
        // arbiter would then have no record of them having decided.
        const boards = getActiveSettings().teamSettings.boards;
        captainDraft.delete(team.id);
        const fallback = window.TeamEngine.defaultLineup(team.squad || [], boards);
        submitLineup({
            tournamentId: state.activeTournament.id, teamId: team.id, round, lineup: fallback
        }).catch(e => console.debug('Could not reset the line-up:', e && e.message));
        renderTeams();
        return;
    }

    const next = { ...(team.lineups || {}) };
    delete next[round];
    team.lineups = next;
    commit();
}

function addToSquad(team, playerId) {
    if (!playerId) return;
    // Guards a stale <select>: the player may have been placed in another team since this
    // panel was last rendered.
    if (teamOfPlayer(playerId, getTeams())) return;
    team.squad = [...(team.squad || []), playerId];
    commit();
}

function wire() {
    if (wired) return;
    wired = true;

    // Delegated from the panel, which is re-rendered wholesale on every change — per-element
    // listeners would go stale and leak.
    const panel = document.getElementById('teams-panel');
    if (!panel) return;

    panel.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-team-action]');
        if (!btn) return;
        const action = btn.dataset.teamAction;

        if (action === 'add') { await addTeam(); return; }
        if (action === 'apply-submissions') { await applySubmissions(); return; }
        if (action === 'from-clubs') {
            if (isArbiterView()) await teamsFromClubs();
            return;
        }

        const team = getTeams().find(t => t.id === btn.dataset.team);
        if (!team) return;

        // Everything except the line-up belongs to the arbiter. The buttons are already
        // hidden for a captain; this is the rule, not the appearance of one.
        const arbiterOnly = ['edit', 'cancel-edit', 'save', 'delete', 'squad-up', 'squad-down', 'squad-remove'];
        if (arbiterOnly.includes(action) && !isArbiterView()) return;

        switch (action) {
            case 'edit': editing.add(team.id); renderTeams(); break;
            case 'cancel-edit': editing.delete(team.id); renderTeams(); break;
            case 'save': saveEdit(team); break;
            case 'delete': await deleteTeam(team); break;
            case 'squad-up': moveInSquad(team, btn.dataset.player, -1); break;
            case 'squad-down': moveInSquad(team, btn.dataset.player, 1); break;
            case 'squad-remove': await removeFromSquad(team, btn.dataset.player); break;
            case 'lineup-reset': resetLineup(team); break;
        }
    });

    panel.addEventListener('change', (e) => {
        const add = e.target.closest('select[data-team-action="squad-add"]');
        if (add) {
            if (!isArbiterView()) return;
            const team = getTeams().find(t => t.id === add.dataset.team);
            if (team) addToSquad(team, add.value);
            return;
        }

        const slot = e.target.closest('select[data-lineup-team]');
        if (slot) {
            const team = getTeams().find(t => t.id === slot.dataset.lineupTeam);
            if (team && canEditLineup(team)) setLineupSlot(team, parseInt(slot.dataset.board, 10), slot.value);
        }
    });
}
